import { useStateValue } from 'providers'
import { refreshAccessToken } from 'actions/api'
import { queryCache } from './queryCache'
import React, { useCallback, useState } from 'react'

const API_URL = process.env.API_URL

export const usePostQuery = ({
  files,
  method = 'POST',
  url,
  headers = {},
  invalidates
} = {}) => {
  const [{ session }, dispatch] = useStateValue()
  const { token } = session
  const [uri, setUrl] = useState(url)
  const [data, setResponseData] = useState(null)
  const [isFetching, setFetching] = useState(false)
  const [error, setError] = useState(null)

  if (token) headers.Authorization = `JWT ${token}`

  const onSuccess = (targetUrl) => {
    queryCache.invalidate(targetUrl)
    if (invalidates) queryCache.invalidateMany(invalidates)
  }

  const post = useCallback(
    async (data, postUrl) => {
      try {
        setFetching(true)
        const response = await fetch(`${API_URL}${postUrl || uri}`, {
          method,
          body: JSON.stringify(data),
          credentials: 'include',
          headers: {
            'Content-Type': files ? 'multipart/form-data;' : 'application/json',
            ...headers
          }
        })
        if (response.status === 401) {
          const refreshed = await refreshAccessToken()
          if (refreshed) {
            const retryResponse = await fetch(`${API_URL}${postUrl || uri}`, {
              method,
              body: JSON.stringify(data),
              headers: {
                'Content-Type': files ? 'multipart/form-data;' : 'application/json',
                ...headers
              },
              credentials: 'include'
            })
            const json = await retryResponse.json()
            setResponseData(json)
            setFetching(false)
            onSuccess(postUrl || uri)
            return json
          } else {
            dispatch({ type: 'LOGOUT' })
            setFetching(false)
            return
          }
        }
        if (response.status < 200 || response.status > 299) {
          throw {
            name: 'Bad Response Status',
            message: `The server responded with ${response.status}: ${response.statusText}`
          }
        }
        const json = await response.json()

        setResponseData(json)
        setFetching(false)
        onSuccess(postUrl || uri)
        return json
      } catch (error) {
        setError(error)
        setFetching(false)
      }
    },
    [headers, uri, invalidates]
  )

  return { data, isFetching, error, post, setUrl }
}
